import { Icon } from "semantic-ui-react";
import { Area, AreaChart, ResponsiveContainer, YAxis } from "recharts";
import { colors } from "../../../style";
import { Card } from "../../ui";
import { chart, formatMinutes, formatNumber } from "./theme";

const style = {
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "16px",
  },
  tile: {
    padding: "16px 18px 0",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  label: {
    color: colors.textMuted,
    fontSize: "13px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  value: {
    color: colors.text,
    fontSize: "30px",
    fontWeight: 600,
    lineHeight: 1.15,
    marginTop: "8px",
  },
  unit: {
    fontSize: "15px",
    fontWeight: 400,
    color: colors.textLight,
    marginLeft: "3px",
  },
  delta: {
    fontSize: "12.5px",
    marginTop: "6px",
    display: "flex",
    alignItems: "center",
    gap: "2px",
  },
  spark: {
    height: "44px",
    margin: "8px -18px 0",
  },
};

const TILES = [
  {
    key: "calls",
    label: "Calls",
    icon: "phone",
    color: "#18a19c",
    format: (v) => formatNumber(v),
    spark: (d) => d.inbound + d.outbound + d.internal,
  },
  {
    key: "talk_minutes",
    label: "Talk time",
    icon: "clock outline",
    color: "#7d72e3",
    format: (v) => formatMinutes(v),
    spark: (d) => d.talk_minutes,
  },
  {
    key: "avg_sentiment",
    label: "Avg sentiment",
    icon: "smile outline",
    color: "#2cc2bc",
    format: (v) => Math.round(v),
    unit: "/ 100",
    spark: (d) => d.avg_sentiment,
    // Points, not percent: sentiment is already on a 0-100 scale.
    deltaAsPoints: true,
    judged: true,
  },
  {
    key: "summarised_pct",
    label: "Summarised",
    icon: "file alternate outline",
    color: "#c98500",
    format: (v) => Math.round(v),
    unit: "%",
    deltaAsPoints: true,
    judged: true,
  },
];

function KpiTiles({ totals, previous, daily }) {
  return (
    <div style={style.grid}>
      {TILES.map((tile) => (
        <Tile
          key={tile.key}
          tile={tile}
          value={totals[tile.key]}
          previous={previous?.[tile.key]}
          daily={daily}
        />
      ))}
    </div>
  );
}

function Tile({ tile, value, previous, daily }) {
  const sparkData = tile.spark
    ? daily.map((d) => ({ v: tile.spark(d) }))
    : null;

  return (
    <Card style={style.tile}>
      <div style={style.label}>
        <Icon name={tile.icon} style={{ margin: 0 }} />
        {tile.label}
      </div>
      <div style={style.value}>
        {/* null = nothing to average yet (e.g. no summarised calls) */}
        {value === null || value === undefined ? (
          <span style={style.unit}>n/a</span>
        ) : (
          <>
            {tile.format(value)}
            {tile.unit && <span style={style.unit}>{tile.unit}</span>}
          </>
        )}
      </div>
      <Delta
        value={value}
        previous={previous}
        asPoints={tile.deltaAsPoints}
        judged={tile.judged}
      />
      <div style={{ ...style.spark, height: sparkData ? "44px" : "16px" }}>
        {sparkData && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={sparkData}
              margin={{ top: 4, right: 0, bottom: 0, left: 0 }}
            >
              <defs>
                <linearGradient
                  id={`spark-${tile.key}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={tile.color} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={tile.color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <YAxis hide domain={["dataMin", "dataMax"]} />
              <Area
                type="monotone"
                dataKey="v"
                stroke={tile.color}
                strokeWidth={1.5}
                fill={`url(#spark-${tile.key})`}
                connectNulls
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
}

function Delta({ value, previous, asPoints, judged }) {
  if (value === null || previous === undefined || previous === null) {
    return null;
  }

  const diff = asPoints
    ? value - previous
    : previous
      ? ((value - previous) / previous) * 100
      : 0;
  const rounded = Math.round(diff);
  const flat = rounded === 0;
  const up = rounded > 0;

  // Volume changes aren't good or bad in themselves, so only judged metrics
  // get status colour (and always with an arrow + text).
  const color =
    flat || !judged ? colors.textMuted : up ? chart.good : chart.critical;

  return (
    <div style={{ ...style.delta, color }}>
      <Icon
        name={flat ? "minus" : up ? "arrow up" : "arrow down"}
        size="small"
      />
      {Math.abs(rounded)}
      {asPoints ? (Math.abs(rounded) === 1 ? " pt" : " pts") : "%"}
      <span style={{ color: colors.textLight, marginLeft: "4px" }}>
        vs previous period
      </span>
    </div>
  );
}

export default KpiTiles;
